/**
 * useContactDetail — W4.8
 *
 * Holt einen einzelnen Contact inkl. Shares (GET /api/v2/contacts/:id).
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'

// --- Typen ---

export type Share = {
  id: string
  hotspot_id: string
  hotspot_name: string | null
  percentage?: number | null
  fixed_amount?: number | null
  start_date?: string | null
  end_date?: string | null
  tags?: string[]
}

export type ContactDetail = {
  id: number
  name: string
  email: string | null
  phone: string | null
  address: string | null
  dashboard: boolean | null
  monthly_report: boolean
  broadcast_enabled: boolean
  notes: string | null
  helium_wallet: string | null
  btc_wallet: string | null
  eth_wallet: string | null
  usdt_wallet: string | null
  usdc_wallet: string | null
  payment_method: string | null
  currency: string | null
  iban: string | null
  paypal: string | null
  tag_list: string
  shares_count: number
  created_at: string
  updated_at: string
  shares: Share[]
}

export type ContactDetailResponse = {
  data: ContactDetail
}

// --- Hook ---

type UseContactDetailResult = {
  contact: ContactDetail | undefined
  isLoading: boolean
  isError: boolean
  isNotFound: boolean
  refetch: () => void
}

export function useContactDetail(id: string): UseContactDetailResult {
  const query = useApi<ContactDetailResponse>(
    ['contact-detail', id],
    async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.GET('/api/v2/contacts/{id}', {
        params: { path: { id } },
      })

      if (result.response?.status === 404) {
        throw { status: 404, notFound: true }
      }

      return result as { data?: ContactDetailResponse; error?: unknown }
    },
    {
      // FIN-CCP-FIX-9: 404 ist ein definitiver Zustand, kein transienter
      // Netzwerkfehler — ohne dieses Override retried TanStack Query per
      // Default 3x mit exponentiellem Backoff (bis ~7s), waehrenddessen ist
      // weder isLoading noch isError/isNotFound gesetzt (weisse Seite).
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | undefined)?.status
        if (status === 404) return false
        return failureCount < 2
      },
    },
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errorStatus = (query.error as any)?.status
  const isNotFound = query.isError && errorStatus === 404

  return {
    contact: query.data?.data,
    isLoading: query.isLoading,
    isError: query.isError && !isNotFound,
    isNotFound,
    refetch: () => query.refetch(),
  }
}
